import {
  Injectable,
  inject,
  effect,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import YouTubePlayer from 'youtube-player';
import { type YouTubePlayer as YT } from 'youtube-player/dist/types';
import { PlaylistService } from './playlist.service';
import { firstValueFrom } from 'rxjs';

declare global {
  interface Window {
    player: YT | undefined;
    playerService: PlayerService;
  }
}

export type PlayerState = -1 | 0 | 1 | 2 | 3 | 5;

export type PlayerStateName =
  | 'unstarted'
  | 'ended'
  | 'playing'
  | 'paused'
  | 'buffering'
  | 'video cued';

export const PLAYER_STATE_NAMES: Record<PlayerState, PlayerStateName> = {
  [-1]: 'unstarted',
  0: 'ended',
  1: 'playing',
  2: 'paused',
  3: 'buffering',
  5: 'video cued',
} as const;

/**
 * Service to manage the YouTube player.
 * Player events list: https://developers.google.com/youtube/iframe_api_reference#Events
 */

@Injectable({
  providedIn: 'root',
})
export class PlayerService {
  playlistService = inject(PlaylistService);
  injector = inject(Injector);

  player!: YT;
  youtubeId?: string;
  private endedHandled = false;
  private ignoreNextEnded = false;

  private getPlayerOrigin(): string | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }

    return window.location.origin;
  }

  async initPlayer(id: string = 'video-player'): Promise<void> {
    console.log('📺 Initializing YouTube player');
    const origin = this.getPlayerOrigin();

    this.player = YouTubePlayer(id, {
      width: '100%',
      height: '100%',
      playerVars: {
        origin,
        playsinline: 1,
      },
    });
    window.player = this.player;
    window.playerService = this;
    try {
      const nowPlaying = await firstValueFrom(
        this.playlistService.getNowPlaying()
      );
      this.playlistService.nowPlaying.set(nowPlaying);
      this.loadVideo(nowPlaying.youtubeId);
      console.log('🎬 Now playing:', nowPlaying.title);
      this.getNextVideoData();
      this.subscribeEvents();
    } catch (err) {
      console.error('❌ Failed to initialize YouTube player:', err);
    }
  }

  subscribeEvents(): void {
    this.playerStateLogger();
    this.playNextEvent();
  }

  getNextVideoData(): void {
    this.playlistService.loadNext();
  }

  playNextEvent(): void {
    this.player.on('stateChange', async event => {
      const state = event.data as PlayerState;
      if (state === 0) {
        if (this.ignoreNextEnded) {
          this.ignoreNextEnded = false;
          return;
        }
        if (this.endedHandled) {
          return;
        }
        this.endedHandled = true;
        console.log('🎬 Video ended, advancing playlist');
        try {
          const result = await firstValueFrom(
            this.playlistService.advancePlaylist()
          );
          console.log('📼 Advanced to', result.newPosition);
          runInInjectionContext(this.injector, () => {
            const effectRef = effect(() => {
              const next = this.playlistService.nextVideo();
              if (next.youtubeId) {
                console.log('▶️ Playing next video:', next.title);
                this.playNextVideo(next.youtubeId);
                this.playlistService.nowPlaying.set(next);
                effectRef.destroy();
              }
            });
          });
        } catch (err) {
          console.error('❌ Playlist advance failed', err);
          this.endedHandled = false;
        }
      }
    });
  }

  playerStateLogger(): void {
    this.player.on('stateChange', event => {
      const state = event.data as PlayerState;
      const stateName = PLAYER_STATE_NAMES[state];
      console.log(`🎮 Player state changed: ${state} (${stateName})`);
    });
  }

  loadVideo(videoId: string = '14RGbE-mZ3Y', ignoreEnded = false) {
    this.endedHandled = false;
    this.ignoreNextEnded = ignoreEnded;
    this.player.loadVideoById(videoId);
    this.youtubeId = videoId;
  }

  playNextVideo(videoId: string) {
    this.endedHandled = false;
    this.ignoreNextEnded = false;
    this.player.loadVideoById(videoId);
    this.youtubeId = videoId;
    this.getNextVideoData();
  }

  play() {
    this.player.playVideo();
  }

  pause() {
    this.player.pauseVideo();
  }

  async seekToEnd(): Promise<void> {
    const duration = await this.player.getDuration();
    this.player.seekTo(duration - 1, true);
  }

  getCurrentVideoId(): string | undefined {
    return this.youtubeId;
  }
}
